//cspell:ignore walink UPCHCP UPCH
import { useCallback } from 'react';

import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';

export const URL_SHORTENER_PREFIX = 'https://l.eulb.me/';

const findUrlsInternal = (message: string): string[] | null => {
  const emailRegex = /\b[\w.%+-]+@[A-Za-z\d.-]+\.[A-Z|a-z]{2,}\b/g;
  const emails = message.match(emailRegex) || [];

  let tempMessage = message;
  const emailPlaceholders: string[] = [];

  emails.forEach((email, index) => {
    const placeholder = `__EMAIL_PLACEHOLDER_${index}__`;
    emailPlaceholders.push(placeholder);
    tempMessage = tempMessage.replace(email, placeholder);
  });

  const urlRegex =
    /(?:https?:\/\/)?(?:[a-z\d]+(?::[a-z\d]+)?@)?(?:www\.)?[a-z\d][\w.-]*[a-z\d]\.[a-z]{2,}(?::\d+)?(?:\/[^\s"'<>]*[^\s"'.<>?!,:;])?/gi;
  const potentialUrls = tempMessage.match(urlRegex);
  if (!potentialUrls) return null;

  const validUrls = potentialUrls
    .map((url) => {
      let cleanedUrl = url;
      while (/[.,!?;:]$/.test(cleanedUrl) && !/[?&]$/.test(cleanedUrl.slice(0, -1))) {
        cleanedUrl = cleanedUrl.slice(0, -1);
      }
      return cleanedUrl;
    })
    .filter((url) => {
      if (url.includes('__EMAIL_PLACEHOLDER_')) {
        return false;
      }

      const parts = url.split('.');
      const lastPart = parts[parts.length - 1];
      return lastPart && lastPart.length >= 2 && !/^\d+$/.test(lastPart) && !url.endsWith('.');
    })
    .map((url) => {
      let restoredUrl = url;
      emailPlaceholders.forEach((placeholder, index) => {
        restoredUrl = restoredUrl.replace(placeholder, emails[index]);
      });
      return restoredUrl;
    });

  return validUrls.length > 0 ? validUrls : null;
};

export const useWhatsAppVariableProcessor = () => {
  const isTrackingUrlWhatsAppEnabled = useFeatureFlag('enableTrackingUrlWhatsApp');
  const isUPCHCPEnabled = useFeatureFlag('UPCH_CP');

  const processText = useCallback(
    (
      text: string | undefined,
      currentVariables: Record<string, string>,
      currentIndex: number
    ): {
      updatedText: string | undefined;
      updatedVariables: Record<string, string>;
      nextIndex: number;
    } => {
      if (text === undefined) {
        return {
          updatedText: undefined,
          updatedVariables: currentVariables,
          nextIndex: currentIndex,
        };
      }

      let newText = text;
      let tempIndex = currentIndex;
      const tempVariables = { ...currentVariables };

      const urls = findUrlsInternal(newText);
      if (urls) {
        for (const url of urls) {
          if (url.startsWith(URL_SHORTENER_PREFIX)) {
            continue;
          }
          const escapedUrl = url.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
          const regexUrl = new RegExp(escapedUrl);

          const replacementUrl = isTrackingUrlWhatsAppEnabled
            ? `${URL_SHORTENER_PREFIX}{{${tempIndex}}}`
            : `{{${tempIndex}}}`;
          newText = newText.replace(regexUrl, replacementUrl);
          tempVariables[tempIndex.toString()] = url;
          tempIndex++;
        }
      }

      // Regex to capture the variable type (e.g., web_url, walink)
      const variableRegex = /\$\s*\{\s*\d+\s*#([^}]+)\}/g;
      newText = newText.replace(variableRegex, (match: string, variableType: string) => {
        tempVariables[tempIndex.toString()] = match; // Store the original variable, e.g., {$1#web_url}
        let replacement = `{{${tempIndex}}}`;

        if (
          isTrackingUrlWhatsAppEnabled &&
          isUPCHCPEnabled &&
          (variableType.toLowerCase() === 'web_url' || variableType.toLowerCase() === 'walink') // temporary magic strings
        ) {
          replacement = `${URL_SHORTENER_PREFIX}${replacement}`;
        }

        tempIndex++;
        return replacement;
      });

      return {
        updatedText: newText,
        updatedVariables: tempVariables,
        nextIndex: tempIndex,
      };
    },
    [isTrackingUrlWhatsAppEnabled, isUPCHCPEnabled]
  );

  const processActionUrls = useCallback(
    (
      actions: Array<{ title: string; url: string }> | undefined,
      currentVariables: Record<string, string>,
      currentIndex: number
    ): {
      processedActions: Array<{ title: string; url: string }>;
      updatedVariables: Record<string, string>;
      nextIndex: number;
    } => {
      if (!actions) {
        return {
          processedActions: [],
          updatedVariables: currentVariables,
          nextIndex: currentIndex,
        };
      }

      let tempIndex = currentIndex;
      const tempVariables = { ...currentVariables };
      const resultingActions: Array<{ title: string; url: string }> = [];

      for (const action of actions) {
        if (action.url && !action.url.startsWith(URL_SHORTENER_PREFIX)) {
          if (isTrackingUrlWhatsAppEnabled) {
            tempVariables[tempIndex.toString()] = action.url;
            const shortenedActionUrl = `${URL_SHORTENER_PREFIX}{{${tempIndex}}}`;
            tempIndex++;
            resultingActions.push({ ...action, url: shortenedActionUrl });
          } else {
            resultingActions.push({ ...action, url: action.url });
          }
        } else {
          resultingActions.push(action);
        }
      }
      return {
        processedActions: resultingActions,
        updatedVariables: tempVariables,
        nextIndex: tempIndex,
      };
    },
    [isTrackingUrlWhatsAppEnabled]
  );

  return { processText, processActionUrls };
};
